/**
 * Modal Component
 * 
 * Generic reusable modal using Radix UI Dialog
 * - Accessible by default (ARIA, keyboard navigation)
 * - Multiple size variants
 * - Smooth animations
 * - Responsive design
 * - Alianzo design system
 * 
 * @component
 * @example
 * <Modal
 *   open={isOpen}
 *   onClose={() => setIsOpen(false)}
 *   title="Edit User"
 *   description="Update user information"
 *   size="md"
 * >
 *   <UserForm />
 * </Modal>
 */

import { FC } from 'react';
import * as Dialog from '@radix-ui/react-dialog';
import { FiX } from 'react-icons/fi';
import type { ModalProps } from './Modal.types';
import styles from './Modal.module.css';

const Modal: FC<ModalProps> = ({
  open,
  onClose,
  title,
  description,
  children,
  size = 'md',
  showCloseButton = true,
  contentClassName,
  preventClose = false,
}) => {
  /**
   * Handle overlay click
   */
  const handleOpenChange = (isOpen: boolean) => {
    if (!isOpen && !preventClose) {
      onClose();
    }
  };

  /**
   * Handle escape key
   */
  const handleEscapeKeyDown = (event: KeyboardEvent) => {
    if (preventClose) {
      event.preventDefault();
    }
  };

  return (
    <Dialog.Root open={open} onOpenChange={handleOpenChange}>
      <Dialog.Portal>
        <Dialog.Overlay className={styles.overlay} />
        <Dialog.Content
          className={`${styles.content} ${contentClassName || ''}`}
          data-size={size}
          onEscapeKeyDown={handleEscapeKeyDown}
          onPointerDownOutside={(e) => {
            if (preventClose) {
              e.preventDefault();
            }
          }}
        >
          {/* Header */}
          <div className={styles.header}>
            <div className={styles.headerContent}>
              <Dialog.Title className={styles.title}>
                {title}
              </Dialog.Title>
              {description && (
                <Dialog.Description className={styles.description}>
                  {description}
                </Dialog.Description>
              )}
            </div>

            {/* Close Button */}
            {showCloseButton && (
              <Dialog.Close asChild>
                <button
                  className={styles.closeButton}
                  aria-label="Fechar modal"
                  type="button"
                >
                  <FiX size={20} />
                </button>
              </Dialog.Close>
            )}
          </div>

          {/* Body */}
          <div className={styles.body}>
            {children}
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
};

export default Modal;
